import { useCreateSession, useRunAgentSse } from '@ltvco/refresh-lib/hooks';
import { useSession } from '@ltvco/refresh-lib/v1';
import { createContext, ReactNode, useCallback, useState } from 'react';

import { DeepSearchConversation, DeepSearchMessage } from '../types';

import type { A2uiMessage } from '@a2ui/web_core/v0_9';

const APP_NAME = 'deepsearch_agent';

interface DeepSearchContextType {
  conversations: DeepSearchConversation[];
  activeConversationId: string | null;
  activeMessages: DeepSearchMessage[];
  isLoading: boolean;
  sendMessage: (content: string) => void;
  startNewConversation: () => void;
  selectConversation: (conversationId: string) => void;
}

export const DeepSearchContext = createContext<DeepSearchContextType>({
  conversations: [],
  activeConversationId: null,
  activeMessages: [],
  isLoading: false,
  sendMessage: () => {},
  startNewConversation: () => {},
  selectConversation: () => {},
});

interface AgentEvent {
  content?: {
    role?: string;
    parts?: Array<{ text?: string }>;
  };
}

function parseA2uiBlock(
  text: string
): { before: string; messages: A2uiMessage[] } | null {
  const match = text.match(/```a2ui\n([\s\S]*?)\n```/);
  if (!match) {
    return null;
  }

  const messages: A2uiMessage[] = [];
  for (const line of match[1].split('\n')) {
    const trimmed = line.trim();
    if (!trimmed) {
      continue;
    }
    try {
      messages.push(JSON.parse(trimmed) as A2uiMessage);
    } catch {
      // skip invalid lines
    }
  }

  if (messages.length === 0) {
    return null;
  }

  return { before: text.slice(0, match.index).trim(), messages };
}

function buildAssistantMessages(text: string): DeepSearchMessage[] {
  const parsed = parseA2uiBlock(text);
  if (parsed) {
    const result: DeepSearchMessage[] = [];
    if (parsed.before) {
      result.push({
        id: crypto.randomUUID(),
        data: parsed.before,
        role: 'assistant',
      });
    }
    result.push({
      id: crypto.randomUUID(),
      data: parsed.messages,
      role: 'assistant',
    });
    return result;
  }
  return [{ id: crypto.randomUUID(), data: text, role: 'assistant' }];
}

export const DeepSearchProvider = ({ children }: { children: ReactNode }) => {
  const { session } = useSession();
  const userId = session.account?.account.user_info?.user_code ?? '';
  const createSessionMutation = useCreateSession();
  const runAgentSseMutation = useRunAgentSse();

  const [conversations, setConversations] = useState<DeepSearchConversation[]>(
    []
  );
  const [activeConversationId, setActiveConversationId] = useState<
    string | null
  >(null);
  const [isLoading, setIsLoading] = useState(false);

  const activeMessages =
    conversations.find((c) => c.id === activeConversationId)?.messages ?? [];

  const addMessageToConversation = useCallback(
    (conversationId: string, message: DeepSearchMessage) => {
      setConversations((prev) =>
        prev.map((c) =>
          c.id === conversationId
            ? { ...c, messages: [...c.messages, message] }
            : c
        )
      );
    },
    []
  );

  const startNewConversation = useCallback(() => {
    setActiveConversationId(null);
  }, []);

  const selectConversation = useCallback(
    (conversationId: string) => {
      const exists = conversations.some((c) => c.id === conversationId);
      if (exists) {
        setActiveConversationId(conversationId);
      }
    },
    [conversations]
  );

  const sendMessage = useCallback(
    async (content: string) => {
      if (!content.trim() || isLoading || !userId) {
        return;
      }

      setIsLoading(true);

      let conversationId = activeConversationId;
      const streamingMsgId = crypto.randomUUID();

      try {
        if (!conversationId) {
          const session = await createSessionMutation.mutateAsync({
            params: { appName: APP_NAME, userId },
          });
          setConversations((prev) => [
            { id: session.id, messages: [] },
            ...prev,
          ]);
          setActiveConversationId(session.id);
          conversationId = session.id;
        }

        const activeId = conversationId!;

        addMessageToConversation(activeId, {
          id: crypto.randomUUID(),
          data: content.trim(),
          role: 'user',
        });

        const response = await runAgentSseMutation.mutateAsync({
          appName: APP_NAME,
          userId,
          sessionId: activeId,
          newMessage: {
            role: 'user',
            parts: [{ text: content.trim() }],
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error ${response.status}`);
        }

        if (!response.body) {
          throw new Error('No response body');
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let lastText = '';

        let chunk = await reader.read();
        while (!chunk.done) {
          buffer += decoder.decode(chunk.value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() ?? '';

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            const raw = line.slice(6).trim();
            if (!raw || raw === '[DONE]') continue;

            try {
              const event = JSON.parse(raw) as AgentEvent;
              const text = event?.content?.parts?.[0]?.text;
              if (text) {
                lastText = text;
                setConversations((prev) =>
                  prev.map((c) =>
                    c.id === activeId
                      ? {
                          ...c,
                          messages: c.messages.map((m) =>
                            m.id === streamingMsgId ? { ...m, data: text } : m
                          ),
                        }
                      : c
                  )
                );
              }
            } catch {
              // skip invalid JSON
            }
          }

          chunk = await reader.read();
        }

        const finalMessages = lastText
          ? buildAssistantMessages(lastText)
          : [
              {
                id: crypto.randomUUID(),
                data: 'Something went wrong. Please try again.',
                role: 'assistant' as const,
              },
            ];

        setConversations((prev) =>
          prev.map((c) =>
            c.id === activeId
              ? {
                  ...c,
                  messages: [
                    ...c.messages.filter((m) => m.id !== streamingMsgId),
                    ...finalMessages,
                  ],
                }
              : c
          )
        );
      } catch {
        setConversations((prev) =>
          prev.map((c) => {
            if (c.id !== conversationId) return c;
            return {
              ...c,
              messages: [
                ...c.messages.filter((m) => m.id !== streamingMsgId),
                {
                  id: crypto.randomUUID(),
                  data: 'Something went wrong. Please try again.',
                  role: 'assistant' as const,
                },
              ],
            };
          })
        );
      } finally {
        setIsLoading(false);
      }
    },
    [
      isLoading,
      activeConversationId,
      userId,
      createSessionMutation,
      runAgentSseMutation,
      addMessageToConversation,
    ]
  );

  return (
    <DeepSearchContext.Provider
      value={{
        conversations,
        activeConversationId,
        activeMessages,
        isLoading,
        sendMessage,
        startNewConversation,
        selectConversation,
      }}
    >
      {children}
    </DeepSearchContext.Provider>
  );
};
